import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import AuthWrapper from "./components/AuthWrapper.tsx";
import FeedbackButton from "./components/FeedbackButton.tsx";
import { ErrorBoundary, ErrorProvider, useError } from "./context/ErrorContext.tsx";
import { UserProvider } from "./context/UserContext.tsx";
import MUIProvider from "./lib/mui-provider.tsx";
import CollectForm from "./pages/CollectForm.tsx";
import Index from "./pages/Index.tsx";
import LandingPage from "./pages/LandingPage.tsx";
import Privacy from "./pages/Privacy.tsx";
import Terms from "./pages/Terms.tsx";

// Wrapper component to handle global errors
const ErrorHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setHasError } = useError();

  useEffect(() => {
    const handleError = () => {
      setHasError(true);
      
      // Reset the error state after animation completes
      setTimeout(() => {
        setHasError(false);
      }, 5000);
    };

    // Add event listener for uncaught errors
    window.addEventListener('error', handleError);
    
    // Add event listener for unhandled promise rejections
    window.addEventListener('unhandledrejection', handleError);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, [setHasError]);

  return <>{children}</>;
};

const AppContent = () => {
  const { hasError } = useError();
  
  return (
    <BrowserRouter>
      <ErrorHandler>
        <Routes>
          {/* Public routes */}
          <Route 
            path="/" 
            element={
              <AuthWrapper requireAuth={false}>
                <LandingPage />
              </AuthWrapper>
            } 
          />
          <Route 
            path="/privacy" 
            element={
              <AuthWrapper requireAuth={false}>
                <Privacy />
              </AuthWrapper>
            } 
          />
          <Route 
            path="/terms" 
            element={
              <AuthWrapper requireAuth={false}>
                <Terms />
              </AuthWrapper>
            } 
          />
          <Route 
            path="/app" 
            element={
              <AuthWrapper requireAuth={false}>
                <Index />
              </AuthWrapper>
            } 
          />
          
          {/* Collection form route */}
          <Route 
            path="/collect/:linkCode" 
            element={<CollectForm />} 
          />
          
          {/* Protected routes */}
          <Route path="/auth/callback" element={<Navigate to="/app?auth_success=true" replace />} />
          <Route path="/auth/error" element={<Navigate to="/" replace />} />
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <FeedbackButton hasError={hasError} />
      </ErrorHandler>
    </BrowserRouter>
  );
};

const App = () => {
  return (
    <MUIProvider>
      <ErrorProvider>
        <UserProvider>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </UserProvider>
      </ErrorProvider>
    </MUIProvider>
  );
};

export default App;
